import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, Mic, AlertCircle, MessageSquare, FileText, CheckCircle2, ChevronRight, Check, Sparkles, Receipt, Building2, Briefcase, Zap, FileDown } from "lucide-react";
import BuildIntelLogo from "../components/BuildIntelLogo";
import PublicTrustFooter from "../components/PublicTrustFooter";
import LandingFaq from "../components/landing/LandingFaq";

// -- MOCKUP HEADER --
// Kept identical to the live in-app header (Layout.jsx) so the transition
// from landing → dashboard feels seamless.
function MockHeader() {
  return (
    <div className="flex items-center gap-2">
      <BuildIntelLogo size={22} testId="mockup-logo" animated={false} />
      <div className="font-heading font-extrabold text-[13px] tracking-tight leading-none text-white">
        BUILDINTEL <span className="text-[#FF9500] drop-shadow-[0_0_8px_rgba(255,149,0,0.5)]">AI</span>
      </div>
    </div>
  );
}

// -- CAROUSEL SLIDES DATA --
// Each slide mirrors a real BuildIntel screen — same tokens, same copy,
// same visual hierarchy as the shipped product. Do NOT invent features.
const PHONE_SLIDES = [
  {
    id: "capture",
    caption: "Snap it. Speak it. Done.",
    render: () => (
      <div className="flex flex-col h-full bg-[#1F1F1F] text-white p-3 font-sans relative">
        <div className="flex items-center justify-between mb-4">
          <MockHeader />
        </div>
        {/* AI Ready · GST-Smart subtitle — verbatim from Capture hero */}
        <div className="flex items-center gap-1.5 text-[#FF9500] mb-1">
          <Sparkles className="w-2.5 h-2.5" />
          <span className="text-[8px] uppercase tracking-[0.2em] font-heading">AI Ready · GST-Smart</span>
        </div>
        <h2 className="font-heading text-lg font-extrabold tracking-tight leading-[1.05] mb-4">
          Snap it. Speak it. <span className="text-[#FF9500]">Done.</span>
        </h2>
        <div className="text-[8px] uppercase tracking-widest text-white/40 font-heading font-bold mb-2">Capture</div>
        <div className="space-y-2.5 flex-1">
          {/* Photo Docket — solid orange, matches PhotoDocket.jsx */}
          <div className="h-20 rounded-2xl bg-[#FF9500] text-[#111827] flex items-center px-3 gap-3 shadow-[0_10px_28px_-14px_rgba(255,149,0,0.55)] relative overflow-hidden">
            <div className="w-10 h-10 rounded-xl bg-[#111827]/15 flex items-center justify-center shrink-0">
              <Camera className="w-5 h-5" strokeWidth={2.2} />
            </div>
            <div className="text-left flex-1 min-w-0">
              <div className="font-heading text-sm font-extrabold leading-tight">Photo Receipt</div>
              <div className="text-[9px] text-[#111827]/80 leading-snug truncate">Snap or pick · AI queues them</div>
            </div>
            <div className="flex items-center gap-0.5 text-[8px] uppercase tracking-wider font-heading font-bold text-[#111827]/70">
              <Zap className="w-2.5 h-2.5" /> Fastest
            </div>
          </div>
          {/* Voice Note — dark bi-elevate, matches VoiceRecorder.tsx */}
          <div className="h-16 rounded-2xl bg-gradient-to-b from-[#2E2E2E] to-[#262626] border border-white/[0.07] flex items-center px-3 gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#FF9500]/15 flex items-center justify-center shrink-0">
              <Mic className="w-5 h-5 text-[#FF9500]" />
            </div>
            <div className="text-left flex-1 min-w-0">
              <div className="font-heading text-sm font-extrabold leading-tight text-white">Voice Note</div>
              <div className="text-[9px] text-white/55 leading-snug truncate">Speak your expense — Whisper types it up</div>
            </div>
          </div>
        </div>
        {/* Bottom nav hint */}
        <div className="mt-3 -mx-3 -mb-3 h-9 border-t border-white/[0.06] bg-[#1a1a1a] flex items-center justify-around text-[8px] font-heading uppercase tracking-widest">
          <span className="text-[#FF9500]">Capture</span>
          <span className="text-white/40">Ledger</span>
          <span className="text-white/40">Jobs</span>
          <span className="text-white/40">Tax AI</span>
        </div>
      </div>
    )
  },
  {
    id: "flags",
    caption: "Fix gaps before they cost you",
    render: () => (
      <div className="flex flex-col h-full bg-[#1F1F1F] text-white p-3 font-sans relative">
        <div className="flex items-center justify-between mb-4">
          <MockHeader />
        </div>
        <div className="flex items-center gap-1.5 text-[#FBBF24] mb-1">
          <AlertCircle className="w-2.5 h-2.5" />
          <span className="text-[8px] uppercase tracking-[0.2em] font-heading">Smart Flags · 4 open</span>
        </div>
        <h2 className="font-heading text-base font-extrabold tracking-tight leading-tight mb-3">
          Fix these before your pack.
        </h2>
        <div className="space-y-2 flex-1">
          {[
            { Icon: Receipt,   label: "No receipt",       hint: "2 entries",  tone: "warn" },
            { Icon: Building2, label: "ABN missing",      hint: "1 subbie",   tone: "warn" },
            { Icon: Briefcase, label: "No job attached",  hint: "1 entry",    tone: "info" },
          ].map(({ Icon, label, hint, tone }) => {
            const cls = tone === "warn"
              ? "text-[#FBBF24] bg-[#FBBF24]/12 border-[#FBBF24]/25"
              : "text-[#60A5FA] bg-[#60A5FA]/10 border-[#60A5FA]/20";
            return (
              <div key={label} className={`rounded-xl border p-2.5 flex items-center gap-2.5 ${cls}`}>
                <Icon className="w-3.5 h-3.5 shrink-0" />
                <div className="text-left flex-1 min-w-0">
                  <div className="text-[11px] font-heading font-bold leading-tight text-white">{label}</div>
                  <div className="text-[9px] leading-tight opacity-80">{hint}</div>
                </div>
                <div className="text-[8px] font-heading font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-current/10 border border-current/30">
                  Fix
                </div>
              </div>
            );
          })}
        </div>
        <div className="text-[8px] text-white/40 text-center leading-snug mt-2">
          Advisory only — flags never block your pack.
        </div>
      </div>
    )
  },
  {
    id: "tax-ai",
    caption: "BAS one-tap, then hand it over",
    render: () => (
      <div className="flex flex-col h-full bg-[#1F1F1F] text-white p-3 font-sans relative">
        <div className="flex items-center justify-between mb-4">
          <MockHeader />
        </div>
        <div className="flex items-center gap-1.5 text-[#FF9500] mb-1">
          <Sparkles className="w-2.5 h-2.5" />
          <span className="text-[8px] uppercase tracking-[0.2em] font-heading">Tax AI · Q2 FY26</span>
        </div>
        <h2 className="font-heading text-base font-extrabold tracking-tight leading-tight mb-3">
          BAS one-tap.
        </h2>
        {/* BAS one-tap card — mirrors BasOneTap component surface */}
        <div className="rounded-2xl bg-gradient-to-b from-[#2E2E2E] to-[#262626] border border-white/[0.07] p-3 mb-2">
          <div className="text-[9px] uppercase tracking-widest text-white/50 font-heading font-bold mb-2">BAS Draft · Q2</div>
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-lg bg-white/[0.04] border border-white/[0.06] p-2">
              <div className="text-[8px] uppercase text-white/45 tracking-wider">Purchases (G11)</div>
              <div className="font-heading font-black text-sm text-white mt-0.5">$8,420</div>
            </div>
            <div className="rounded-lg bg-white/[0.04] border border-white/[0.06] p-2">
              <div className="text-[8px] uppercase text-white/45 tracking-wider">GST (1B)</div>
              <div className="font-heading font-black text-sm text-[#FF9500] mt-0.5">$765</div>
            </div>
          </div>
        </div>
        {/* Ruleset stamp */}
        <div className="text-[8px] text-white/40 mb-2 flex items-center gap-1">
          <CheckCircle2 className="w-2.5 h-2.5 text-[#FF9500]" />
          ATO ruleset 2026.1 · Stamped
        </div>
        {/* Download accountant pack CTA */}
        <div className="mt-auto">
          <div className="h-11 rounded-xl bg-[#FF9500] text-[#111827] flex items-center justify-center gap-1.5 shadow-[0_10px_30px_-15px_rgba(255,149,0,0.6)]">
            <FileDown className="w-3.5 h-3.5" />
            <span className="text-[11px] font-heading font-extrabold uppercase tracking-wider">Download Accountant Pack</span>
          </div>
        </div>
      </div>
    )
  },
  {
    id: "fy-summary",
    caption: "One-page FY summary for your accountant",
    render: () => (
      <div className="flex flex-col h-full bg-[#1F1F1F] text-white p-3 font-sans relative">
        <div className="flex items-center justify-between mb-3">
          <MockHeader />
        </div>
        {/* FY PDF preview card — resembles the actual PDF output */}
        <div className="bg-white text-black flex-1 rounded-lg p-3 shadow-xl overflow-hidden flex flex-col">
          <div className="bg-[#1F1F1F] -m-3 mb-2 px-3 py-2 rounded-t-lg">
            <div className="font-heading font-black text-[11px] text-[#FF9500] leading-none">Mick&apos;s Carpentry</div>
            <div className="text-[7px] text-white/50 uppercase tracking-widest mt-0.5">Year-End Summary · Prepared with BuildIntel AI</div>
          </div>
          <div className="grid grid-cols-3 gap-1.5 mb-2">
            <div className="bg-[#F4F4F5] p-1.5 rounded">
              <div className="text-[6px] text-black/50 uppercase tracking-wider">Business Spend</div>
              <div className="font-heading font-black text-[10px] mt-0.5">$8,420</div>
            </div>
            <div className="bg-[#F4F4F5] p-1.5 rounded">
              <div className="text-[6px] text-black/50 uppercase tracking-wider">GST on Purch</div>
              <div className="font-heading font-black text-[10px] mt-0.5">$765</div>
            </div>
            <div className="bg-[#F4F4F5] p-1.5 rounded">
              <div className="text-[6px] text-black/50 uppercase tracking-wider">Entries</div>
              <div className="font-heading font-black text-[10px] mt-0.5">142</div>
            </div>
          </div>
          <div className="text-[8px] font-bold mb-1">Deductions by category</div>
          <div className="space-y-1.5 flex-1">
            {[
              { label: "Materials",  w: "82%", amt: "$3,240" },
              { label: "Subbies",    w: "62%", amt: "$2,450" },
              { label: "Fuel · Vehicle", w: "38%", amt: "$1,510" },
              { label: "Tools",      w: "22%", amt: "$870" },
            ].map((cat) => (
              <div key={cat.label}>
                <div className="flex justify-between text-[7px] text-black/60 mb-0.5">
                  <span>{cat.label}</span><span className="font-bold">{cat.amt}</span>
                </div>
                <div className="h-1 w-full bg-black/5 rounded-full overflow-hidden">
                  <div className="h-full bg-[#FF9500] rounded-full" style={{ width: cat.w }} />
                </div>
              </div>
            ))}
          </div>
          <div className="text-[6px] italic text-black/40 mt-2 leading-tight">
            Helper summary. Confirm all figures with your registered tax agent.
          </div>
        </div>
      </div>
    )
  }
];

export default function Landing() {
  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % PHONE_SLIDES.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const headerOffset = 80;
      const elementPosition = el.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({ top: offsetPosition, behavior: "smooth" });
    }
  };

  return (
    <div className="min-h-screen bg-[#1F1F1F] bi-grain bi-ambient text-[#F9FAFB] overflow-x-hidden selection:bg-[#FF9500] selection:text-black" data-testid="landing-page">

      {/* Top Nav — safe-area-inset-top pushes SIGN UP below the iOS
          status bar / battery pill on installed PWAs and Safari with
          the URL bar collapsed. */}
      <header
        className="fixed top-0 left-0 right-0 z-50 bg-[#1F1F1F]/85 backdrop-blur-xl border-b border-white/[0.08]"
        style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 md:h-20 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 group min-w-0">
            <BuildIntelLogo size={32} className="sm:hidden" testId="landing-nav-logo-mobile" />
            <BuildIntelLogo size={36} className="hidden sm:block" testId="landing-nav-logo" />
            <div className="flex flex-col justify-center min-w-0">
              <span className="font-heading font-extrabold text-base sm:text-lg tracking-tight leading-none text-white whitespace-nowrap">
                BUILDINTEL <span className="text-[#FF9500] drop-shadow-[0_0_8px_rgba(255,149,0,0.5)]">AI</span>
              </span>
              <span className="hidden sm:inline text-[10px] uppercase tracking-widest text-[#FF9500] font-bold mt-0.5">Built for AU Tradies</span>
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-3 shrink-0">
            <a
              href="/login"
              data-testid="landing-nav-login"
              className="text-[13px] sm:text-sm font-medium text-white/70 hover:text-white transition-colors min-h-[44px] px-2 sm:px-3 flex items-center whitespace-nowrap"
            >
              Log in
            </a>
            <a
              href="/register"
              data-testid="landing-nav-signup"
              className="h-10 sm:h-11 px-4 sm:px-5 ml-1 sm:ml-2 rounded-full bi-accent-btn text-xs sm:text-sm font-heading font-bold uppercase tracking-wider inline-flex items-center whitespace-nowrap"
            >
              Sign up
            </a>
          </div>
        </div>
      </header>

      <main className="relative z-10 pt-32 md:pt-48 pb-24">
        {/* HERO SECTION — mobile shows the phone mockup ABOVE the copy so
            visitors immediately see what the app does; desktop grid layout
            unchanged (lg:col-start / lg:col-span rules take precedence over
            the mobile flex order utilities). `dvh` (dynamic viewport height)
            respects the mobile browser URL bar so the caption pill under
            the mock isn't clipped by Safari's bottom toolbar. */}
        <section className="max-w-7xl mx-auto px-4 md:px-8 grid lg:grid-cols-12 gap-8 lg:gap-8 items-center min-h-[70dvh] pb-8 lg:pb-0" data-testid="landing-hero">
          <div className="lg:col-span-6 flex flex-col items-start pt-4 lg:pt-0 relative bi-hero-glow order-2 lg:order-1">

            <div className="inline-flex items-center gap-2 border border-white/[0.08] bg-white/[0.04] rounded-full px-3 py-1 mb-6" data-testid="landing-soft-launch-chip">
              <span className="bi-pulse" style={{ background: "#FF9500", boxShadow: "0 0 0 0 rgba(255,149,0,0.55)" }} />
              <span className="text-xs font-semibold tracking-wide text-white/75">Soft launch</span>
            </div>

            <h1 className="font-heading font-black text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[1.05] text-white mb-6 text-balance">
              Photo a receipt.<br/>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF9500] to-[#FFB057]">Send your accountant a pack.</span>
            </h1>

            <p className="text-lg md:text-xl text-white/60 leading-relaxed mb-10 max-w-xl">
              The glove-friendly AI assistant for Aussie tradies. Snap receipts, speak your notes, and BuildIntel puts together an accountant-ready pack you can review before you send.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
              <a
                href="/register"
                data-testid="landing-hero-cta-signup"
                className="h-14 rounded-xl bi-accent-btn bi-cta-pulse px-8 inline-flex items-center justify-center gap-2 font-heading font-bold uppercase tracking-wider text-sm group"
              >
                Sign up free
                <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </a>
              <button
                onClick={() => scrollToSection("features")}
                data-testid="landing-hero-cta-see-how"
                className="h-14 rounded-xl bi-ghost-btn px-8 inline-flex items-center justify-center font-heading font-bold uppercase tracking-wider text-sm"
              >
                See how it works
              </button>
            </div>

            {/* Trust Bar Mini */}
            <div className="mt-12 flex flex-wrap gap-6 items-center border-t border-white/[0.08] pt-6 text-sm text-white/50 font-medium">
              <div className="flex items-center gap-2"><CheckCircle2 size={16} className="text-[#FF9500]" /> ATO Ruleset Stamped</div>
              <div className="flex items-center gap-2"><CheckCircle2 size={16} className="text-[#FF9500]" /> Receipts stay yours</div>
            </div>
          </div>

          <div className="lg:col-span-5 lg:col-start-8 flex flex-col items-center justify-center mt-0 lg:mt-0 relative order-1 lg:order-2">
             {/* Abstract rings behind phone */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-[300px] h-[300px] border border-white/5 rounded-full absolute" />
              <div className="w-[450px] h-[450px] border border-white/5 rounded-full absolute" />
            </div>

            {/* PHONE MOCKUP (Pure CSS) */}
            <div data-testid="landing-phone-mockup" className="relative w-[280px] h-[580px] bg-[#0A0A0A] rounded-[40px] border-[6px] border-[#2A2A2A] shadow-2xl shadow-black/80 overflow-hidden ring-1 ring-white/10 flex flex-col">
              {/* Top Notch/Dynamic Island */}
              <div className="absolute top-0 inset-x-0 h-6 flex justify-center z-20">
                <div className="w-24 h-5 bg-[#2A2A2A] rounded-b-2xl" />
              </div>
              
              {/* Power Button accent on side frame (visual only, rendered via pseudo in CSS if needed, but keeping it simple here) */}
              
              <div className="flex-1 bg-[#111] relative overflow-hidden mt-6 rounded-t-3xl">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeSlide}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 1.05 }}
                    transition={{ duration: 0.4, ease: [0.2, 0.8, 0.2, 1] }}
                    className="absolute inset-0"
                    data-testid={`landing-phone-slide-${PHONE_SLIDES[activeSlide].id}`}
                  >
                    {PHONE_SLIDES[activeSlide].render()}
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Home indicator */}
              <div className="absolute bottom-2 inset-x-0 flex justify-center z-20">
                <div className="w-24 h-1 bg-white/20 rounded-full" />
              </div>
            </div>

            {/* Carousel Caption Sync */}
            <div className="mt-8 bg-white/5 backdrop-blur-md border border-white/10 rounded-full px-5 py-2.5 max-w-[320px] text-center shadow-lg">
               <AnimatePresence mode="wait">
                 <motion.p
                   key={activeSlide}
                   initial={{ opacity: 0, y: 5 }}
                   animate={{ opacity: 1, y: 0 }}
                   exit={{ opacity: 0, y: -5 }}
                   className="text-sm font-medium text-[#FF9500]"
                 >
                   {PHONE_SLIDES[activeSlide].caption}
                 </motion.p>
               </AnimatePresence>
            </div>
          </div>
        </section>

        {/* HOW IT WORKS STRIP */}
        <section className="max-w-7xl mx-auto px-4 md:px-8 mt-24" data-testid="landing-how">
          <div className="bi-elevate rounded-2xl px-6 md:px-10 py-10">
            <div className="grid md:grid-cols-3 gap-8 text-center md:text-left divide-y md:divide-y-0 md:divide-x divide-white/[0.08]">
              {[
                { step: "01", title: "Snap & Speak", desc: "Open the app, photograph your receipt, and speak a 5-second context note." },
                { step: "02", title: "BuildIntel Organises", desc: "AI extracts line items, tags the job, and spots missing ABNs instantly." },
                { step: "03", title: "Hand to Accountant", desc: "Generate a flawless, ATO-versioned PDF summary at quarter or year end." }
              ].map((s, i) => (
                <div key={s.step} className={`pt-6 md:pt-0 ${i !== 0 ? 'md:pl-8' : ''} bi-rise bi-rise-${i + 1}`}>
                  <div className="text-[#FF9500] font-heading font-black text-2xl opacity-70 mb-2">{s.step}</div>
                  <div className="text-lg font-heading font-bold text-white mb-2">{s.title}</div>
                  <div className="text-sm text-white/60 leading-relaxed">{s.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FEATURES BENTO GRID */}
        <section id="features" className="max-w-7xl mx-auto px-4 md:px-8 py-32">
          <div className="mb-16 md:text-center">
            <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-tight text-white mb-4">
              Built for the site, not the desk.
            </h2>
            <p className="text-lg text-white/50 max-w-2xl mx-auto">
              Everything you need to keep your business admin tight, without spending your weekends in spreadsheets.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">

            {/* Main Feature: Voice Capture */}
            <div data-testid="landing-feature-card-voice-capture" className="md:col-span-8 group relative bi-elevate rounded-3xl p-8 overflow-hidden bi-rise bi-rise-1 hover:brightness-[1.05] transition-[filter]">
              <div className="absolute top-0 right-0 p-6 opacity-10 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                <Mic size={120} className="text-[#FF9500] rotate-12" />
              </div>
              <div className="relative z-10 h-full flex flex-col justify-end min-h-[240px]">
                <div className="bg-[#FF9500]/20 text-[#FF9500] w-12 h-12 rounded-2xl flex items-center justify-center mb-6 border border-[#FF9500]/25">
                  <Mic size={24} />
                </div>
                <h3 className="font-heading font-bold text-3xl text-white mb-3">Voice Capture</h3>
                <p className="text-white/60 text-lg max-w-md leading-relaxed">
                  Speak your expense. Whisper AI types it up with flawless construction terminology. No tiny keyboards.
                </p>
              </div>
            </div>

            {/* Secondary: FY Summary */}
            <div data-testid="landing-feature-card-fy-summary" className="md:col-span-4 group relative bi-elevate rounded-3xl p-8 overflow-hidden bi-rise bi-rise-2 hover:brightness-[1.05] transition-[filter]">
              <div className="relative z-10 flex flex-col h-full">
                <div className="bg-white/5 text-white w-12 h-12 rounded-2xl flex items-center justify-center mb-6 border border-white/[0.08] group-hover:border-[#FF9500]/40 transition-colors">
                  <FileText size={24} />
                </div>
                <h3 className="font-heading font-bold text-2xl text-white mb-3 mt-auto">FY Summary</h3>
                <p className="text-white/60 text-base leading-relaxed">
                  One-page year-end PDF perfectly formatted for your accountant. ATO-versioned logic baked in.
                </p>
              </div>
            </div>

            {/* Tertiary: Smart Flags */}
            <div data-testid="landing-feature-card-smart-flags" className="md:col-span-6 group relative bi-elevate rounded-3xl p-8 overflow-hidden bi-rise bi-rise-3 hover:brightness-[1.05] transition-[filter]">
              <div className="relative z-10">
                <div className="bg-white/5 text-white w-12 h-12 rounded-2xl flex items-center justify-center mb-6 border border-white/[0.08] group-hover:border-[#FF9500]/40 transition-colors">
                  <AlertCircle size={24} />
                </div>
                <h3 className="font-heading font-bold text-2xl text-white mb-3">Smart Flags</h3>
                <p className="text-white/60 text-base leading-relaxed">
                  Spot missing receipts, unlinked ABNs, or duplicate entries instantly before you export the pack.
                </p>
              </div>
            </div>

            {/* Quaternary: Glove-Friendly UI */}
            <div data-testid="landing-feature-card-glove-friendly" className="md:col-span-6 group relative bi-elevate rounded-3xl p-8 overflow-hidden bi-rise bi-rise-4 hover:brightness-[1.05] transition-[filter]">
              <div className="absolute -right-10 -bottom-10 w-48 h-48 bg-[#FF9500]/5 rounded-full blur-2xl group-hover:bg-[#FF9500]/10 transition-colors duration-500 pointer-events-none" />
              <div className="relative z-10">
                <div className="bg-white/5 text-white w-12 h-12 rounded-2xl flex items-center justify-center mb-6 border border-white/[0.08] group-hover:border-[#FF9500]/40 transition-colors">
                  <CheckCircle2 size={24} />
                </div>
                <h3 className="font-heading font-bold text-2xl text-white mb-3">Glove-Friendly UI</h3>
                <p className="text-white/60 text-base leading-relaxed">
                  Massive tap targets, high-contrast text, and simple flows. Built to work even when your hands are full.
                </p>
              </div>
            </div>

          </div>
        </section>

        {/* WHAT YOU GET — concise trust bullets before the invite */}
        <section id="what-you-get" className="max-w-4xl mx-auto px-4 md:px-8 pb-8" data-testid="landing-what-you-get">
          <div className="bi-elevate rounded-2xl p-6 md:p-10">
            <div className="mb-8 text-center">
              <div className="inline-flex items-center gap-1.5 text-[#FF9500] mb-3">
                <Sparkles className="w-3.5 h-3.5" />
                <span className="text-[11px] uppercase tracking-[0.2em] font-heading font-bold">What you get</span>
              </div>
              <h2 className="font-heading font-black text-3xl md:text-4xl tracking-tight text-white">
                Everything a tradie needs.<br className="hidden sm:block" /> Nothing they don&apos;t.
              </h2>
            </div>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
              {[
                "Snap or speak receipts on site",
                "Jobs + GST in one place",
                "Smart flags before send",
                "Accountant / BAS packs with evidence",
                "ATO ruleset stamp — helper only",
              ].map((item, i) => (
                <li
                  key={item}
                  data-testid={`landing-benefit-${i + 1}`}
                  className={`flex items-start gap-3 py-2 ${i === 4 ? "md:col-span-2" : ""}`}
                >
                  <span className="mt-0.5 shrink-0 w-6 h-6 rounded-full bg-[#FF9500]/15 border border-[#FF9500]/30 flex items-center justify-center">
                    <Check className="w-3.5 h-3.5 text-[#FF9500]" strokeWidth={3} />
                  </span>
                  <span className="text-white/85 text-base leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* CLOSING SIGN-UP CTA — open soft launch, no gating */}
        <section id="get-started" className="max-w-3xl mx-auto px-4 md:px-8 py-20" data-testid="landing-get-started">
          <div className="bi-elevate rounded-[2rem] p-8 md:p-12 relative overflow-hidden text-center">
            <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#FF9500]/50 to-transparent" />
            <div className="inline-flex items-center gap-2 border border-white/[0.08] bg-white/[0.04] rounded-full px-3 py-1 mb-6">
              <span className="bi-pulse" style={{ background: "#FF9500", boxShadow: "0 0 0 0 rgba(255,149,0,0.55)" }} />
              <span className="text-[11px] font-semibold tracking-wide text-white/75 uppercase">Early access</span>
            </div>
            <h2 className="font-heading font-black text-3xl md:text-4xl text-white mb-3">
              Ready to tidy your receipts?
            </h2>
            <p className="text-white/60 text-base md:text-lg leading-relaxed max-w-lg mx-auto mb-2">
              Create your BuildIntel account in under a minute. No credit card. Start capturing on your next job.
            </p>
            <p className="text-white/40 text-xs mb-8 italic">Free while we learn with tradies.</p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center max-w-md mx-auto">
              <a
                href="/register"
                data-testid="landing-get-started-signup"
                className="h-14 rounded-xl bi-accent-btn bi-cta-pulse px-8 inline-flex items-center justify-center gap-2 font-heading font-bold uppercase tracking-wider text-sm group flex-1"
              >
                Sign up free
                <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="/login"
                data-testid="landing-get-started-login"
                className="h-14 rounded-xl bi-ghost-btn px-8 inline-flex items-center justify-center font-heading font-bold uppercase tracking-wider text-sm"
              >
                Log in
              </a>
            </div>
          </div>
        </section>

      </main>

      {/* QUESTIONS — Felix-style accordion, only one panel open at a
          time. Copy locked with the founder / accountant; no CTAs
          allowed inside answers. Sits directly above the trust
          footer so a visitor's last impression is "confirm with your
          accountant" — not marketing hype. */}
      <LandingFaq testId="landing-faq" />

      {/* FOOTER — compact, no repeated logo/brand mark or trade chip above
          the trust line. PublicTrustFooter renders the disclaimer, policy
          links, and copyright in a single tight block. */}
      <footer className="border-t border-white/[0.08] bg-[#1a1a1a] pt-8 pb-8 relative z-10">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <PublicTrustFooter testId="landing-trust-footer" />
        </div>
      </footer>
    </div>
  );
}
